import React, { useState, useCallback } from "react";

import {
  Popover,
  Tooltip,
  IconButton,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  makeStyles
} from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";
import { FilterList } from "@material-ui/icons";
import { KNOWN_CREATIVE_TYPES } from "../../utils/dataUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: "24px 24px 36px 24px",
    minWidth: "300px",
    maxWidth: "300px"
  },
  header: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  title: {
    display: "inline-block",
    marginLeft: "7px",
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500
  },
  select: {
    marginTop: "16px"
  }
}));

export const DEFAULT_CHANGELOG_FILTERS = {
  profileName: "All",
  creativeType: "All",
  advertiser: "All",
  date: "All"
};

export const ChangeLogFilter = ({ changelog, dcmCreativesMap, sqlCreativeTypeMap = {}, profileEmailMap, filters, setFilters, allDataLoaded = false, isMasterAdmin = false, advertiserMap = {}, sqlAdvertiserIdMap = {} }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const profileEmails = [...new Set(
    changelog.map((l) => profileEmailMap[l.userProfileId] || l.userProfileName).filter(Boolean)
  )].sort();

  const creativeTypes = KNOWN_CREATIVE_TYPES;

  const advertiserNames = isMasterAdmin ? [...new Set(
    changelog.map((l) => {
      const advId = sqlAdvertiserIdMap[l.objectId] || dcmCreativesMap[l.objectId]?.advertiserId;
      return advId ? advertiserMap[String(advId)] : null;
    }).filter((name) => name && !name.includes("BidManager") && !name.includes("DONOTUSE"))
  )].sort() : [];

  const dates = [...new Set(
    changelog.map((l) => new Date(l.changeTime).toLocaleDateString())
  )].sort((a, b) => new Date(b) - new Date(a));

  const handleFilter = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_CHANGELOG_FILTERS);
  }, [setFilters]);

  const activeFilterCount = Object.values(filters).filter((v) => v !== "All").length;

  return (
    <>
      <Tooltip title="Filter" disableFocusListener>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <FilterList color={activeFilterCount > 0 ? "primary" : "inherit"} />
        </IconButton>
      </Tooltip>
      <Popover
        disableScrollLock={true}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Paper className={classes.root}>
          <div className={classes.header}>
            <Typography className={classes.title} variant="h6">
              Filter Results
            </Typography>
            <Tooltip title="Reset All">
              <IconButton onClick={handleReset}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Grid container>
            <Grid className={classes.select} item xs={12}>
              <TextField
                select
                onChange={(e) => handleFilter("profileName", e.target.value)}
                value={filters.profileName}
                fullWidth
                variant="outlined"
                margin="dense"
                label="Email"
              >
                <MenuItem value="All">All</MenuItem>
                {profileEmails.map((email) => (
                  <MenuItem key={email} value={email}>
                    {email}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid className={classes.select} item xs={12}>
              <TextField
                select
                onChange={(e) => handleFilter("creativeType", e.target.value)}
                value={filters.creativeType}
                fullWidth
                variant="outlined"
                margin="dense"
                label="Creative Type"
              >
                <MenuItem value="All">All</MenuItem>
                {creativeTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              {!allDataLoaded && filters.creativeType !== "All" && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                  Loading more data...
                </Typography>
              )}
            </Grid>

            {isMasterAdmin && (
              <Grid className={classes.select} item xs={12}>
                <TextField
                  select
                  onChange={(e) => handleFilter("advertiser", e.target.value)}
                  value={filters.advertiser}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Advertiser"
                >
                  <MenuItem value="All">All</MenuItem>
                  {advertiserNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid className={classes.select} item xs={12}>
              <TextField
                select
                onChange={(e) => handleFilter("date", e.target.value)}
                value={filters.date}
                fullWidth
                variant="outlined"
                margin="dense"
                label="Date"
              >
                <MenuItem value="All">All</MenuItem>
                {dates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      </Popover>
    </>
  );
};
